// src/admin/pages/sold-vehicles/soldVehicleService.ts
// The single place a car's status ever becomes 'Sold' with real buyer/
// payment details attached — whether the sale started from a Reservation
// (deposit-paid customer) or is a direct walk-in/pre-owned sale with no
// prior reservation. See CarDrawer.tsx, which no longer exposes 'Sold' as a
// plain status option for exactly this reason.
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../../../firebase/config';
import { AuditService } from '../audit/auditService';
import { ReservationService } from '../reservation/reservationService';
import { TradeInService } from '../tradein/tradeInService';
import { RegistrationTransferStatus, SaleDocument } from '../../../enums/product';

export interface RecordSaleInput {
  carId: string;
  carName: string;
  buyerName: string;
  buyerPhone: string;
  buyerEmail?: string;
  buyerIdNumber?: string;
  buyerAddress?: string;
  finalSalePrice: number;
  depositAmount?: number;
  paymentMethod?: string;
  deliveryMethod?: string;
  deliveryDate?: string;
  saleNotes?: string;
  reservationId?: string;
  salespersonId?: string;
  salespersonName?: string;
  tradeInId?: string;
  tradeInValue?: number;
  financingProvider?: string;
  financingTermMonths?: number;
  monthlyInstallment?: number;
  registrationTransferStatus?: RegistrationTransferStatus;
  warrantyMonths?: number;
  warrantyNotes?: string;
  documents?: SaleDocument[];
}

export class SoldVehicleService {
  /**
   * Upload related documents (title deed, invoice, ID copy, warranty card,
   * etc.) for a sold vehicle to Firebase Storage.
   */
  static async uploadDocuments(carId: string, files: File[]): Promise<SaleDocument[]> {
    return Promise.all(
      files.map(async (file) => {
        const timestamp = Date.now();
        const storageRef = ref(storage, `soldVehicleDocuments/${carId}/${timestamp}_${file.name}`);
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
        return { name: file.name, url, uploadedAt: new Date().toISOString() };
      })
    );
  }

  static async deleteDocument(document: SaleDocument): Promise<void> {
    try {
      await deleteObject(ref(storage, document.url));
    } catch (e) {
      console.warn('Error deleting document:', e);
    }
  }

  static async recordSale(input: RecordSaleInput, userId: string, userName: string): Promise<void> {
    const carRef = doc(db, 'products', input.carId);
    await updateDoc(carRef, {
      status: 'Sold',
      buyerName: input.buyerName,
      buyerPhone: input.buyerPhone,
      buyerEmail: input.buyerEmail || null,
      buyerIdNumber: input.buyerIdNumber || null,
      buyerAddress: input.buyerAddress || null,
      finalSalePrice: input.finalSalePrice,
      depositAmount: input.depositAmount || 0,
      paymentMethod: input.paymentMethod || null,
      deliveryMethod: input.deliveryMethod || null,
      deliveryDate: input.deliveryDate || null,
      soldByUserId: userId,
      soldByName: userName,
      soldAt: serverTimestamp(),
      reservationId: input.reservationId || null,
      salespersonId: input.salespersonId || null,
      salespersonName: input.salespersonName || null,
      saleNotes: input.saleNotes || '',
      tradeInId: input.tradeInId || null,
      tradeInValue: input.tradeInValue || 0,
      financingProvider: input.paymentMethod === 'Financing' ? (input.financingProvider || null) : null,
      financingTermMonths: input.paymentMethod === 'Financing' ? (input.financingTermMonths || null) : null,
      monthlyInstallment: input.paymentMethod === 'Financing' ? (input.monthlyInstallment || null) : null,
      registrationTransferStatus: input.registrationTransferStatus || 'Not Started',
      warrantyMonths: input.warrantyMonths || 0,
      warrantyNotes: input.warrantyNotes || '',
      documents: input.documents || [],
      updatedAt: serverTimestamp(),
    });

    if (input.reservationId) {
      try {
        await ReservationService.update(
          input.reservationId,
          {
            status: 'Sold',
            finalSalePrice: input.finalSalePrice,
            depositAmount: input.depositAmount,
            paymentMethod: input.paymentMethod as any,
            deliveryMethod: input.deliveryMethod as any,
          },
          userId,
          userName
        );
      } catch (e) {
        console.error('Could not sync linked reservation to Sold:', e);
      }
    }

    // Mark the trade-in as spent so it can't be applied to a second sale.
    if (input.tradeInId) {
      try {
        await TradeInService.update(
          input.tradeInId,
          { appliedToSaleCarId: input.carId },
          userId,
          userName
        );
      } catch (e) {
        console.error('Could not mark trade-in as applied to sale:', e);
      }
    }

    await AuditService.log({
      action: 'status_change',
      module: 'Sold Vehicles',
      entityId: input.carId,
      entityLabel: input.carName,
      description: `Recorded sale of "${input.carName}" to ${input.buyerName} for ${input.finalSalePrice.toLocaleString()}`,
      userId,
      userName,
    });
  }
}
